import type { ApiActivityLog, ApiOrgProperty, ApiOrgPropertyFacts, ApiPropertyProject, ApiScan, ApiUpdateOrgProperty } from '@kaiizen/kai-pro-api';

// *************************************************************
// The property's facts
// *************************************************************

/** The property facts the overview shows and edits, as the strings its inputs hold. */
export type PropertyFactsDraft = {
  bedroomCount: string;
  fullBathCount: string;
  halfBathCount: string;
  squareFeet: string;
  acres: string;
  hasGarage: '' | 'yes' | 'no';
  orgMarketId: string;
  propertyId: string;
};

const toDraftString = (value: number | string | null | undefined) => value == null ? '' : String(value);

export const getPropertyFactsDraft = (orgProperty: ApiOrgProperty): PropertyFactsDraft => {
  const facts = orgProperty.customerMetadata ?? {};
  return {
    bedroomCount: toDraftString(facts.bedroomCount),
    fullBathCount: toDraftString(facts.fullBathCount),
    halfBathCount: toDraftString(facts.halfBathCount),
    squareFeet: toDraftString(facts.squareFeet),
    acres: toDraftString(facts.acres),
    hasGarage: facts.hasGarage == null ? '' : facts.hasGarage ? 'yes' : 'no',
    orgMarketId: orgProperty.orgMarketId ?? '',
    propertyId: toDraftString(facts.propertyId),
  };
};

const parseNumber = (value: string) => {
  if (value.trim() === '') return null;
  const parsed = Number(value);
  return Number.isNaN(parsed) ? null : parsed;
};

/**
 * The property update a saved draft makes. Facts merge into the existing `customerMetadata`, as the
 * legacy property card's edits do, and `bathroomCount` stays the full plus half baths. The market
 * is only sent when it changed.
 */
export const getPropertyFactsUpdate = (draft: PropertyFactsDraft, orgProperty: ApiOrgProperty): ApiUpdateOrgProperty => {
  const fullBathCount = parseNumber(draft.fullBathCount);
  const halfBathCount = parseNumber(draft.halfBathCount);
  const facts: Partial<ApiOrgPropertyFacts> = {
    bedroomCount: parseNumber(draft.bedroomCount),
    fullBathCount,
    halfBathCount,
    bathroomCount: fullBathCount == null && halfBathCount == null ? null : (fullBathCount ?? 0) + (halfBathCount ?? 0),
    squareFeet: parseNumber(draft.squareFeet),
    acres: parseNumber(draft.acres),
    hasGarage: draft.hasGarage === '' ? null : draft.hasGarage === 'yes',
    propertyId: draft.propertyId.trim() === '' ? null : draft.propertyId.trim(),
  } as Partial<ApiOrgPropertyFacts>;
  return {
    customerMetadata: { ...orgProperty.customerMetadata, ...facts },
    ...(draft.orgMarketId && draft.orgMarketId !== orgProperty.orgMarketId ? { orgMarketId: draft.orgMarketId } : {}),
  } as ApiUpdateOrgProperty;
};

// *************************************************************
// Jobs (projects)
// *************************************************************

export type PropertyJobsView = 'active' | 'archived';

const time = (date: Date | string | undefined) => date ? new Date(date).getTime() : 0;

/**
 * The jobs one view of the table lists, by when they were last updated. On an archived property
 * every job is archived with it, so the Active view shows them all rather than none.
 */
export const getPropertyJobsForView = (
  projects: readonly ApiPropertyProject[],
  view: PropertyJobsView,
  { propertyIsArchived = false, newestFirst = true }: { propertyIsArchived?: boolean; newestFirst?: boolean } = {},
) =>
  projects
    .filter(project => propertyIsArchived ? view === 'active' : (view === 'archived') === !!project.archivedAt)
    .sort((left, right) => newestFirst ? time(right.updatedAt) - time(left.updatedAt) : time(left.updatedAt) - time(right.updatedAt));

export const getPropertyJobCounts = (projects: readonly ApiPropertyProject[], { propertyIsArchived = false } = {}) => {
  const archived = propertyIsArchived ? 0 : projects.filter(project => project.archivedAt).length;
  return { active: projects.length - archived, archived };
};

/** Material plus labor on a scan row — the same rollup persisted after scan-data writes. */
export const getScanTotalAmount = (scan: Pick<ApiScan, 'totalLaborCost' | 'totalMaterialCost'>): number =>
  (scan.totalMaterialCost ?? 0) + (scan.totalLaborCost ?? 0);

export type PropertyJobScopeMoney = {
  /** First approved scope total; absent until the job has an approved scope. */
  initialScope?: number;
  /** Current scope total minus initial; absent when there is no approved scope or no net change. */
  changes?: number;
  /** Latest approved scope total, or the working scan when nothing is approved yet. */
  total?: number;
};

/**
 * Job-table scope dollars from embedded project scans — no line-item diff. Initial scope is the
 * first approved version; total is the latest approved scope (or the in-progress scan before
 * approval); changes is the delta between them.
 */
export const getPropertyJobScopeMoney = (project: ApiPropertyProject): PropertyJobScopeMoney => {
  const initialScan = project.initialApprovedScopeScan;
  const currentScan = project.latestApprovedScan ?? project.latestScan;
  if (!currentScan) return {};

  const total = getScanTotalAmount(currentScan);
  if (!initialScan) return { total };

  const initialScope = getScanTotalAmount(initialScan);
  const changes = total - initialScope;
  return {
    initialScope,
    changes: changes === 0 ? undefined : changes,
    total,
  };
};

/** Sum of job totals for every job at the property (Active and Archived), for the Lifetime footer. */
export const getPropertyJobsLifetimeTotal = (projects: readonly ApiPropertyProject[]) =>
  projects.reduce((sum, project) => sum + (getPropertyJobScopeMoney(project).total ?? 0), 0);

/** The header's figures, derived from the same jobs the table lists so the two always agree. */
export const getPropertyHeadFigures = (projects: readonly ApiPropertyProject[]) => {
  const lastUpdated = projects.reduce<Date | string | undefined>(
    (latest, project) => time(project.updatedAt) > time(latest) ? project.updatedAt : latest,
    undefined,
  );
  return {
    activeJobCount: projects.filter(project => project.status === 'active' && !project.archivedAt).length,
    lastUpdated,
  };
};

// *************************************************************
// Activity
// *************************************************************

const JOB_EVENT_TITLES: Partial<Record<NonNullable<ApiActivityLog['eventType']>, string>> = {
  projectCreated: 'Job created',
  projectCompleted: 'Job completed',
  projectReactivated: 'Job reopened',
  projectArchived: 'Job archived',
  projectUnarchived: 'Job unarchived',
};

/** What a property's activity row says happened ("Job created"), or undefined for an entry it doesn't show. */
export const getPropertyActivityTitle = (entry: Pick<ApiActivityLog, 'eventType'>) => entry.eventType ? JOB_EVENT_TITLES[entry.eventType] : undefined;

// *************************************************************
// Dates
// *************************************************************

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** "Sep 14" over "2026", the way the tables stack a date. */
export const getStackedDate = (date: Date | string | undefined) => {
  if (!date) return undefined;
  const value = new Date(date);
  return { day: `${MONTHS[value.getMonth()]} ${value.getDate()}`, year: String(value.getFullYear()) };
};

/** "Sep 14, 2026". */
export const formatShortDate = (date: Date | string | undefined) => {
  const stacked = getStackedDate(date);
  return stacked ? `${stacked.day}, ${stacked.year}` : undefined;
};
